import React, { useEffect, useState } from "react";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { Tooltip } from "components/common/Tooltip";
import { TooltipButton } from "components/common/TooltipButton";
import { Inline } from "components/common/Stack";
import {
  ContextMenu,
  type ContextMenuItem,
} from "components/common/ContextMenu";
import { useContextMenu } from "components/hooks/useContextMenu";
import {
  isPostgresLike,
  type SavedConnection,
} from "components/canvas/leftSide/import/Postgres/savedConnectionsApi";
import {
  useConnectionsStore,
  type ConnectionHealthEntry,
} from "stores/connectionsStore";
import { BUCKET_PROVIDER_TAG, ConnectionTypeIcon } from "./ConnectionTypeIcon";
import { formatBytes } from "components/canvas/detailsSide/utils/formatBytes";
import { formatRelativeTime } from "./formatRelativeTime";
import {
  deriveSqliteHandleState,
  type SqliteHandleState,
} from "stores/sqliteHandles";

const HEALTH_DOT_CLASS: Record<ConnectionHealthEntry["state"], string> = {
  checking: "bg-fg-muted",
  reachable: "bg-success",
  unreachable: "bg-error",
};

const healthAriaLabel = (h: ConnectionHealthEntry): string =>
  h.state === "checking"
    ? "Checking connection"
    : h.state === "reachable"
      ? "Connection reachable"
      : "Connection unreachable";

const healthTooltip = (h: ConnectionHealthEntry): string => {
  if (h.state === "checking") return "Checking connection…";
  const checked = h.checkedAt
    ? ` · checked ${formatRelativeTime(h.checkedAt)}`
    : "";
  return h.state === "reachable"
    ? `Reachable${checked}`
    : `Unreachable — ${h.reason ?? "connection failed"}${checked}`;
};

interface Props {
  connection: SavedConnection;
  onEdit: (c: SavedConnection) => void;
  onDelete: (c: SavedConnection) => void;
  /** Briefly flashes `bg-list-selected` then fades to the resting bg.
   * Driven by ConnectionsPage's `?highlight=<id>` URL handling. */
  highlighted?: boolean;
}

/// One connection in the list. Mirrors the Datasets row idiom — a flat
/// `group/card` row with hover-bg (no card border) — but connections don't
/// expand: clicking the row opens the edit modal.
export const ConnectionRow: React.FC<Props> = ({
  connection,
  onEdit,
  onDelete,
  highlighted = false,
}) => {
  const menu = useContextMenu();
  const cr = connection.credentials;
  const meta =
    cr.type === "google_oauth"
      ? cr.account_email
      : cr.type === "bucket"
        ? `${BUCKET_PROVIDER_TAG[cr.provider]} · ${
            cr.scope.kind === "bucket"
              ? cr.scope.name
              : "all buckets in account"
          }`
        : cr.type === "local_sqlite"
          ? `Local file · ${formatBytes(cr.file_size)}`
          : `${cr.host}:${cr.port}/${cr.database} · ${cr.username}`;

  const isBucket = cr.type === "bucket";
  const isLocalSqlite = cr.type === "local_sqlite";

  const healthEntry = useConnectionsStore((s) =>
    isPostgresLike(cr) ? s.health[connection.id] : undefined,
  );

  // Per-device handle state for LocalSqlite rows (v1.2.1, S5b). The
  // FSA handle lives in IndexedDB on this device only — if it's
  // missing or permission was revoked, we surface a "re-attach"
  // pill so the user knows the connector won't open on this
  // machine until they re-attach the file. The TableDetails
  // freshness check stays silent for these rows (it can't run
  // without a handle); the Connections page is the single place
  // that prompts recovery.
  const [sqliteState, setSqliteState] = useState<SqliteHandleState | null>(
    null,
  );
  useEffect(() => {
    if (!isLocalSqlite) return;
    let cancelled = false;
    void (async () => {
      const state = await deriveSqliteHandleState(connection.id);
      if (!cancelled) setSqliteState(state);
    })();
    return () => {
      cancelled = true;
    };
  }, [isLocalSqlite, connection.id]);
  const needsReattach =
    sqliteState?.kind === "needs-reattach-this-device" ||
    sqliteState?.kind === "handle-revoked";

  // Buckets and local SQLite have no editable credentials — the modal is a
  // details / re-attach view for them, matching the row's hover affordance.
  const isEditable = !isBucket && !isLocalSqlite;

  const contextMenuItems: ContextMenuItem[] = [
    {
      label: isEditable ? "Edit" : "See details",
      icon: isEditable ? (
        <EditOutlinedIcon className="icon-md" />
      ) : (
        <VisibilityOutlinedIcon className="icon-md" />
      ),
      onClick: () => onEdit(connection),
    },
    { label: "", onClick: () => {}, divider: true },
    {
      label: "Delete",
      icon: <DeleteOutlineOutlinedIcon className="icon-md" />,
      onClick: () => onDelete(connection),
      danger: true,
    },
  ];

  return (
    <div
      id={`connection-${connection.id}`}
      className={`rounded-app group/card${highlighted ? " highlight-flash" : ""}`}
    >
      <div
        className={`group flex items-center gap-2 px-1 py-1.5 rounded-app select-none cursor-pointer group-hover/card:bg-bg-hover transition-colors ${
          menu.isOpen ? "bg-bg-hover" : ""
        }`}
        tabIndex={0}
        onClick={() => onEdit(connection)}
        {...menu.triggerProps}
      >
        <span className="flex items-center justify-center size-6 shrink-0">
          <ConnectionTypeIcon type={cr.type} size={20} />
        </span>

        <div className="min-w-0 flex-1">
          <Inline gap="xs" className="min-w-0 items-center">
            <span
              className="truncate text-fg"
              style={{
                fontSize: "var(--font-size)",
                fontWeight: "var(--font-weight-medium)",
              }}
            >
              {connection.display_name}
            </span>
          </Inline>
          <span className="text-fg-muted-regular-small font-mono truncate block leading-tight">
            {healthEntry && (
              <Tooltip title={healthTooltip(healthEntry)}>
                <span
                  role="img"
                  aria-label={healthAriaLabel(healthEntry)}
                  className={`inline-block w-1.5 h-1.5 rounded-full align-middle mr-1.5 ${HEALTH_DOT_CLASS[healthEntry.state]}`}
                />
              </Tooltip>
            )}
            {meta}
          </span>
          {needsReattach && (
            <span
              className="text-small leading-tight block"
              style={{ color: "var(--color-warning)" }}
            >
              {sqliteState?.kind === "handle-revoked"
                ? "Permission revoked · re-attach this file on this device"
                : "Not attached on this device · re-attach to open"}
            </span>
          )}
        </div>

        <div
          className={`flex items-center gap-2 shrink-0 transition-opacity ${
            menu.isOpen
              ? ""
              : "opacity-0 group-hover/card:opacity-100 focus-within:opacity-100"
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          {isEditable && (
            <TooltipButton
              title="Edit"
              variant="contextual"
              size="xs"
              onClick={() => onEdit(connection)}
              aria-label={`Edit ${connection.display_name}`}
            >
              <EditOutlinedIcon sx={{ fontSize: 14 }} />
            </TooltipButton>
          )}
        </div>
      </div>

      <ContextMenu
        items={contextMenuItems}
        position={menu.position}
        onClose={menu.close}
        ariaLabel={`Actions for ${connection.display_name}`}
      />
    </div>
  );
};
